import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { selectIncompleteUploadedFileIds, selectUserFileRecordsByServerId } from '@/uppy-file-upload/store/selectors'
import { selectCurrentFolderProcessingFileIds, selectLoadedUserFilesById } from '@/user_files/files/store/selectors'
import { UserFileProcessingBlockedReason } from '@/user_files/schemas/processing-status'

/**
 * Combines all user file polling sources into a single list of file ids for a single centralized file polling processor.
 * Current sources include:
 * - Uppy/user-files uploads still pending or processing
 * - Files in the currently loaded folder still processing
 */
export const selectCombinedUserFilePollingIds = createSelector(
  [
    selectIncompleteUploadedFileIds,
    selectCurrentFolderProcessingFileIds,
    (state: RootState) => state.userFilesPollingState.sourcesById,
    selectLoadedUserFilesById,
    selectUserFileRecordsByServerId,
  ],
  (drawerFileIds, currentFolderFileIds, sourcesById, loadedFilesById, uploadFilesById): string[] => {
    const seenIds = new Set<string>()
    const dedupedIds: string[] = []
    const quotaBlockedIds = new Set<string>()

    for (const filesById of [loadedFilesById, uploadFilesById]) {
      for (const [fileId, file] of Object.entries(filesById)) {
        if (file.processing_blocked_reason === UserFileProcessingBlockedReason.OcrQuota) {
          quotaBlockedIds.add(fileId)
        }
      }
    }

    const addFileId = (fileId: string) => {
      if (seenIds.has(fileId) || quotaBlockedIds.has(fileId)) return
      seenIds.add(fileId)
      dedupedIds.push(fileId)
    }

    for (const fileId of [...drawerFileIds, ...currentFolderFileIds]) {
      addFileId(fileId)
    }

    for (const { fileIds } of Object.values(sourcesById)) {
      for (const fileId of fileIds) {
        addFileId(fileId)
      }
    }

    return dedupedIds
  }
)
